/**
 * Web component: main-navigation (main-navigation)
 * -----------------------------------------------------------------------------
 * main-navigation.
 * - Component settings: section / limited / web / vanilla
 *
 */
import './main-navigation.scss';
import { vueReady } from '~/core/runners';
import * as webComponent from '~/helpers/web-components';
import useOverlaysStore from '~stores/overlays';

/**
 * DOM selectors.
 */
const selectors = {
  link: '[js-main-navigation="link"]',
  backBtn: '[js-main-navigation="back"]',
  navLiLvl1: '[js-main-navigation="nav-li-lvl1"]',
  naviList: '[js-main-navigation="nav-list"]',
  navigation: '[js-main-navigation="navigation"]',
};

/**
 * Create new <main-navigation> custom element.
 */
class MainNavigation extends HTMLElement {
  static observedAttributes = [];

  attributeChangedCallback(name, oldValue, newValue) {
    webComponent.handleAttributeUpdate(this, name, newValue, oldValue);
  }

  data(component) {
    return {
      selectors: {
        link: component.querySelectorAll(selectors.link),
        backBtn: component.querySelectorAll(selectors.backBtn),
        navLiLvl1: component.querySelectorAll(selectors.navLiLvl1),
        naviList: component.querySelector(selectors.naviList),
        navigation: component.querySelector(selectors.navigation),
      },
    };
  }

  computed() {
    return {};
  }

  watch() {
    return {};
  }

  constructor() {
    super();
    this.$data = {};
    this.overlay = useOverlaysStore();
  }

  connectedCallback() {
    this.$data = this.data(this);
    webComponent.setup(this);

    mobileHandling(this);
    copyPromocodeToClipboard(this);
    desktopTeaserDatalayerPushHover(this);
    mobileBackButton(this);

    const marketSelectorMobile = document.querySelector(
      '#market-selector-mobile',
    );
    if (marketSelectorMobile) {
      marketSelectorMobile.addEventListener('click', () => {
        const marketSelectorDesktop = document.querySelector(
          '#market-selector-desktop',
        );
        if (marketSelectorDesktop) {
          marketSelectorDesktop.click();
        }
        closeNavi(this);
      });
    }

    const closeButton = document.querySelector('.main-navigation__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        closeNavi(this);
      });
    }

    const overlay = document.querySelector('.main-navigation__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        closeNavi(this);
      });
    }
  }

  setEventListeners() {
    if (!this.$data.selectors.example) {
      return;
    }

    this.$data.selectors.example.addEventListener('click', () => {
      this.handleClick(this);
    });
  }
}

function copyPromocodeToClipboard() {
  const promoBtn = document.querySelector(
    '.main-navigation__dropdown-teaser-promo-button',
  );
  const promoCode = document.querySelector(
    '.main-navigation__dropdown-teaser-promo-code',
  )?.innerText;
  if (promoBtn) {
    promoBtn.addEventListener('click', () => {
      navigator.clipboard
        .writeText(promoCode)
        .then(() => {
          promoBtn.classList.add('show-tooltip');

          setTimeout(() => {
            promoBtn.classList.remove('show-tooltip');
          }, 1000);
        })
        .catch(() => {
          cnvs.ReportError('Failed to copy promo code');
        });
    });
  }
}

/**
 * Mobile navigation handling - Datalayer pushes and event listeners
 */

function mobileHandling(self) {
  const links = self.$data.selectors.link;

  /**
   * Add event listener to all li elements in main navigation
   */

  links.forEach((item) => {
    item.addEventListener('click', (event) => {
      const naviList = self.$data.selectors.naviList;
      const linkTitle = item.getAttribute('data-track');
      const mobileTeaser = item.closest(
        '.main-navigation__teaser-mobile-container',
      );
      const parentTitle = mobileTeaser
        ? ''
        : item
            .closest('.main-navigation__item')
            ?.querySelector('.main-navigation__link')
            .getAttribute('data-track');
      let parentBreadcrumb = item.classList.contains('main-navigation__link')
        ? ''
        : `${parentTitle} > `;
      const parentLvl1 = item?.closest('.main-navigation__item');

      if (window.matchMedia('(max-width: 1023px)').matches) {
        const url = item.getAttribute('data-href');
        /**
         * Redirect to the link if it has data-href attribute and is not a main navigation li element
         */

        if (mobileTeaser) {
          parentBreadcrumb = '';
        }

        if (isSpanDataNoDropdown(item)) {
          location.replace(url);
        }

        /**
         * Add active class to the clicked li element and subnav Dropdown and prevent default for mobile navigation
         */
        if (!mobileTeaser && parentLvl1.classList.contains('has-dropdown')) {
          if (item.hasAttribute('data-navlvl')) {
            event.preventDefault();
          }

          parentLvl1.classList.add('active');
          naviList.classList.add('subnav-active');
        }

        /**
         * Datalayer event for navigation select mobile
         */
        cnvs.EventBus.emit('dataLayer:navigation_select', {
          event: 'navigation_select',
          click_item: `${parentBreadcrumb}${linkTitle}`,
          nav_element: 'header',
        });
      } else {
        /**
         * Redirect to the link if it has data-href attribute and is not a main navigation li element
         */
        if (item.tagName === 'SPAN' && item.hasAttribute('data-href')) {
          const url = item.getAttribute('data-href');
          location.replace(url);
        }

        /**
         * Remove active class from all li elements and subnav Dropdown
         */
        parentLvl1.classList.remove('active');
        naviList.classList.remove('subnav-active');

        /**
         * Datalayer event for navigation select mobile
         */
        cnvs.EventBus.emit('dataLayer:navigation_select', {
          event: 'navigation_select',
          click_item: `${parentBreadcrumb}${linkTitle}`,
          nav_element: 'header',
        });
      }
    });
  });
}

function isSpanDataNoDropdown(element) {
  return (
    element.tagName === 'SPAN' &&
    element.hasAttribute('data-href') &&
    (element.classList.contains('main-navigation__dropdown-view-all') ||
      element.classList.contains('main-navigation__dropdown-title') ||
      !element.closest('li').classList.contains('has-dropdown'))
  );
}

function closeNavi(self) {
  const navigationWrapper = document.querySelector('.main-navigation.liquid');
  const liLvl1 = self.$data.selectors.navLiLvl1;
  const subnav = document.querySelector('.subnav-active');

  navigationWrapper.classList.remove('main-navigation--open');
  if (subnav) {
    subnav.classList.remove('subnav-active');
  }
  liLvl1.forEach((item) => {
    item.classList.remove('active');
  });

  document.querySelector('body').classList.remove('no-scroll');
  self.overlay.close('mainNavigation');
}

function mobileBackButton(self) {
  const backButton = self.$data.selectors.backBtn;

  backButton.forEach((item) => {
    item.addEventListener('click', (event) => {
      const mobileLi = event.target.closest('.main-navigation__item');
      mobileLi.classList.remove('active');
      self.$data.selectors.naviList.classList.remove('subnav-active');
    });
  });
}

function desktopTeaserDatalayerPushHover(self) {
  /**
   * Datalayer event for navigation select mobile on hover teaser element on desktop
   */

  if (window.matchMedia('(min-width: 1024px)').matches) {
    self.$data.selectors.navLiLvl1.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        const teaser = item.querySelectorAll(
          '.main-navigation__dropdown-teaser-container .main-navigation__dropdown-teaser',
        );
        if (teaser.length > 0) {
          for (const tea of teaser) {
            const teaserUrl = tea.getAttribute('href')
              ? tea.getAttribute('href')
              : '';
            const teaserTitle = tea.getAttribute('data-track')
              ? tea.getAttribute('data-track')
              : '';
            cnvs.EventBus.emit(`dataLayer:view_promotion`, {
              event: 'view_promotion',
              promotion: {
                promotion_id: teaserUrl,
                promotion_name: teaserTitle,
                creative_slot: 'header-navigation',
                creative_name: 'Mobile Menu Teaser',
              },
            });
          }
        }
      });
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('main-navigation')) {
    return;
  }

  customElements.define('main-navigation', MainNavigation);
});
